import {
  Children,
  cloneElement,
  ComponentPropsWithRef,
  createContext,
  ReactElement,
  useContext,
  useRef,
} from 'react';
import {
  AriaCheckboxGroupProps,
  mergeProps,
  useCheckboxGroup,
  useCheckboxGroupItem,
} from 'react-aria';
import innerText from 'react-innertext';
import { CheckboxGroupState, useCheckboxGroupState } from 'react-stately';

import Checkbox, { CheckboxProps } from 'design-system/components/Checkbox';

import FormField from './FormField';
import { ImplementsFormField } from './inputFields';

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const CheckboxGroupContext = createContext<CheckboxGroupState>(null);

interface CheckboxGroupItemProps {
  children: ReactElement<ComponentPropsWithRef<typeof Checkbox>>;
}

const CheckboxGroupItem = (props: CheckboxGroupItemProps) => {
  const { children } = props;
  const checkboxProps = children.props;

  const state = useContext(CheckboxGroupContext);
  const ref = useRef();
  const { inputProps } = useCheckboxGroupItem(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    {
      ...checkboxProps,
      onChange: undefined,
      isDisabled: checkboxProps.disabled,
      isReadOnly: checkboxProps.readOnly,
      isRequired: checkboxProps.required,
      isIndeterminate: checkboxProps.indeterminate,
      value: String(children.props.value),
      'aria-label': innerText(checkboxProps.label),
    },
    state,
    ref,
  );

  return cloneElement(
    children,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    mergeProps(inputProps, {
      onChange: checkboxProps.onChange,
      disabled: state.isDisabled || checkboxProps.disabled,
      checked: state.isSelected(String(inputProps.value)),
      ref,
    } as ComponentPropsWithRef<typeof Checkbox>),
  );
};

interface CheckboxGroupFieldProps
  extends ImplementsFormField,
    Omit<
      AriaCheckboxGroupProps,
      'description' | 'disabled' | 'isReadOnly' | 'isRequired' | 'errorMessage'
    > {
  children?: ReactElement<CheckboxProps>[];
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
}

const CheckboxGroupField = ({
  children,
  name,
  onChange,
  disabled,
  readOnly,
  required,
  ...props
}: CheckboxGroupFieldProps) => {
  const reactAriaProps = {
    ...props,
    description: props.helperText,
    errorMessage: props.errorText,
    onChange,
    name,
    isDisabled: disabled,
    isReadOnly: readOnly,
    isRequired: required,
  };
  const state = useCheckboxGroupState(reactAriaProps);
  const { groupProps, labelProps, descriptionProps, errorMessageProps } = useCheckboxGroup(
    reactAriaProps,
    state,
  );

  return (
    <FormField
      labelProps={labelProps}
      helperProps={descriptionProps}
      errorProps={errorMessageProps}
      disabled={disabled}
      {...groupProps}
      {...props}
    >
      <CheckboxGroupContext.Provider value={state}>
        {Children.map(children, (checkbox) => (
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          <CheckboxGroupItem>{checkbox}</CheckboxGroupItem>
        ))}
      </CheckboxGroupContext.Provider>
    </FormField>
  );
};

export default CheckboxGroupField;
